import type { ExcalidrawElement } from "./types";

/**
 * Returns the top-most (highest z-order) element under the given scene point
 * that matches the predicate. Excalidraw's element array is ordered from
 * background to foreground, so iterating in reverse yields the top-most first.
 *
 * Hit-test is an AABB (axis-aligned bounding box) with an optional margin
 * applied to all sides.
 */
export const findTopMostElement = <T extends ExcalidrawElement = ExcalidrawElement>(
  elements: readonly T[],
  x: number,
  y: number,
  predicate: (el: T) => boolean,
  margin = 0,
): T | null => {
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i];
    if (el.isDeleted) {
      continue;
    }
    if (!predicate(el)) {
      continue;
    }
    const x1 = Math.min(el.x, el.x + el.width);
    const x2 = Math.max(el.x, el.x + el.width);
    const y1 = Math.min(el.y, el.y + el.height);
    const y2 = Math.max(el.y, el.y + el.height);
    if (x >= x1 - margin && x <= x2 + margin && y >= y1 - margin && y <= y2 + margin) {
      return el;
    }
  }
  return null;
};
